/**
 * Identidade Visual eBuz - tokens
 * Manual v1.0 (Maio 2026)
 * SDD-implantacao-identidade-visual / 02-MAPEAMENTO-TOKENS.md
 *
 * Use estes constants quando precisar de raw values em TS (ex: inline styles, charts, SVG).
 * Pra UI, prefira Tailwind classes (`bg-brand-500`, `text-ink-0`, `font-display`).
 */

export const BRAND_TOKENS = {
  pink: {
    1: '#EF3476',
    2: '#EA7F76',
  },
  gradient: 'linear-gradient(135deg, #EF3476 0%, #EA7F76 100%)',

  ink: {
    0: '#0A0A0B',
    1: '#1A1A1D',
    2: '#2A2A2E',
    3: '#4A4A52',
    4: '#71717A',
    5: '#A1A1AA',
    6: '#D4D4D8',
    7: '#E8E8EC',
    8: '#F4F4F6',
    9: '#FAFAFB',
  },

  paper: '#FFFFFF',

  semantic: {
    success: '#1FB870',
    successBg: '#E8F8F0',
    warning: '#F5A524',
    warningBg: '#FEF5E5',
    danger: '#E5484D',
    dangerBg: '#FCEBEC',
    info: '#5E6AD2',
    infoBg: '#ECEDFB',
  },

  spacing: {
    1: 4,   // s-1
    2: 8,   // s-2
    3: 12,  // s-3
    4: 16,  // s-4
    5: 24,  // s-5
    6: 32,  // s-6
    7: 48,  // s-7
    8: 64,  // s-8
    9: 96,  // s-9
    10: 128,// s-10
    11: 192,// s-11
  },

  radius: {
    xs: 6,
    sm: 10,
    md: 14,
    lg: 20,
    xl: 28,
  },

  shadow: {
    soft: '0 1px 2px rgba(10,10,11,.04), 0 4px 14px -4px rgba(10,10,11,.06)',
    glow: '0 8px 24px -8px rgba(239,52,118,.4)',
  },

  font: {
    display: 'Sora, system-ui, sans-serif',
    body: 'Manrope, system-ui, sans-serif',
    mono: 'JetBrains Mono, ui-monospace, monospace',
  },

  /** DNA visual: grid 32x32 com linhas sutis */
  gridLine: 'rgba(10,10,11,.05)',
} as const

/**
 * Default theme aplicado quando workspace não tem branding custom.
 * Workspaces podem override apenas:
 * - primaryColor / primaryColorEnd
 * - logoUrl
 * - faviconUrl
 * - siteTitle
 *
 * NÃO podem override: fontes, layout, motifs (DNA fixo da plataforma).
 */
export const EBUZ_DEFAULT_THEME = {
  primaryColor: BRAND_TOKENS.pink[1],
  primaryColorEnd: BRAND_TOKENS.pink[2],
  logoUrl: '/brand/logo-light.png',
  logoLightUrl: '/brand/logo-light.png',
  logoDarkUrl: '/brand/logo-mono-white.png',
  logoMonoBlackUrl: '/brand/logo-mono-black.png',
  faviconUrl: '/brand/favicon-color.png',
  faviconWhiteUrl: '/brand/favicon-white.png',
  siteTitle: 'eBuz',
} as const

export type EbuzTheme = typeof EBUZ_DEFAULT_THEME

/**
 * Manifesto eBuz (vide Manual de Identidade seção 11 - Voz)
 */
export const BRAND_VOICE = {
  tagline: 'Não cria. Gera.',
  headline: 'Da intuição ao ecossistema.',
  principles: [
    {
      n: '01',
      title: 'Origem única',
      body: 'Tudo nasce do mesmo documento. O posicionamento do expert é a fonte da verdade — todos os ativos digitais herdam dele, automaticamente.',
    },
    {
      n: '02',
      title: 'Conexão sobre criação',
      body: 'Não somos uma agência. Não fazemos peça por peça. Geramos um ecossistema onde curso, funil e blog conversam entre si desde o nascimento.',
    },
    {
      n: '03',
      title: 'Sistema, não produto',
      body: 'A ebuz é uma fábrica de presença digital. Cada output é consequência de um input estruturado — não de um briefing repetido a cada peça nova.',
    },
    {
      n: '04',
      title: 'Para experts reais',
      body: 'Mercado massivo de brasileiros com conhecimento preso ao presencial. A ebuz desbloqueia esse conhecimento sem exigir que o expert vire designer.',
    },
  ],
  voiceTraits: [
    { trait: 'Direta', description: 'sem rodeios. uma ideia por frase.' },
    { trait: 'Estrutural', description: 'pensa em sistemas, não peças.' },
    { trait: 'Confiante', description: 'afirma. não suplica. não vende.' },
    { trait: 'Próxima', description: 'fala português. de gente. para gente.' },
  ],
  doSay: [
    'Não cria. Gera.',
    'Um documento. Todos os ativos.',
    'Saia do briefing infinito.',
  ],
  dontSay: [
    'A solução definitiva para sua presença digital',
    'Revolucione seu negócio com IA',
    'Junte-se a milhares de empreendedores de sucesso',
  ],
} as const
